import clsx from 'clsx';
import React, { useRef } from 'react';
import { SwiperOptions } from 'swiper/types';
import ChevronLeft from '~/icons/chevron-left.svg';
import ChevronRight from '~/icons/chevron.svg';
import { Button, Link as SharedLink, Typography, TypographyStyle } from '~/shared/components';
import { Carousel } from '~/shared/components/Carousel';
import { Headline, Link } from '~/lib';
import { breakpointsRaw } from '~/theme/breakpoints';
import styles from './CardCarousel.module.scss';

const defaultOptions = (isCondensed?: boolean): SwiperOptions => ({
    slidesPerView: 'auto',
    slidesPerGroupAuto: true,
    spaceBetween: isCondensed ? 8 : 16,
    breakpoints: {
        [breakpointsRaw.sm]: { spaceBetween: isCondensed ? 24 : 20 },
    },
});

// Hands the gap between cards to CSS, which leaves the same gap after the last one.
const trailingSpace = ({ spaceBetween, breakpoints }: SwiperOptions) => {
    const px = (gap: SwiperOptions['spaceBetween']) =>
        typeof gap === 'number' ? `${gap}px` : undefined;

    return {
        '--carousel-trailing-space': px(spaceBetween),
        '--carousel-trailing-space-sm': px(breakpoints?.[breakpointsRaw.sm]?.spaceBetween),
    } as React.CSSProperties;
};

type CardCarouselProps = {
    headline?: Omit<Headline, 'style'>;
    headlineVariant?: TypographyStyle;

    // List of products to be shown in carousel
    callToAction?: Link;
    options?: SwiperOptions;
    items?: React.ReactNode[];
    className?: string;
    isSkipHidden?: boolean;

    // Reduces the module's spacing, its heading, and the cards and the gaps between them.
    isCondensed?: boolean;
};

export const CardCarousel = ({
    headline,
    items,
    callToAction,
    options,
    className,
    isSkipHidden,
    isCondensed,
    headlineVariant = 'title-S',
}: CardCarouselProps) => {
    const leftArrow = useRef<HTMLButtonElement>(null);
    const rightArrow = useRef<HTMLButtonElement>(null);
    const swiperOptions = { ...defaultOptions(isCondensed), ...options };

    return (
        <div
            className={clsx(styles.component, isCondensed && styles.condensed, className)}
            style={trailingSpace(swiperOptions)}
        >
            <div className={styles.header}>
                {!!headline?.text && (
                    <Typography
                        className={styles.headline}
                        as={headline.tag ?? 'h2'}
                        variant={headlineVariant}
                    >
                        {headline.text}
                    </Typography>
                )}

                {callToAction && (
                    <div>
                        <Button
                            as={SharedLink}
                            aria-label="link"
                            variant="new-link"
                            href={callToAction.url}
                            target={callToAction.target}
                        >
                            {callToAction.title}
                        </Button>
                    </div>
                )}
            </div>

            <div className={styles.carousel}>
                <button
                    aria-label="Previous"
                    ref={leftArrow}
                    className={clsx(styles.navBtn, styles.prev)}
                >
                    <ChevronLeft />
                </button>

                <Carousel
                    className={styles.slider}
                    swiperClassName={styles.sliderSwiper}
                    options={swiperOptions}
                    prevRef={leftArrow}
                    nextRef={rightArrow}
                    items={items}
                    isSkipHidden={isSkipHidden}
                />

                <button
                    aria-label="Next"
                    ref={rightArrow}
                    className={clsx(styles.navBtn, styles.next)}
                >
                    <ChevronRight />
                </button>
            </div>
        </div>
    );
};
